
import { useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";

interface UseDevotionalNavigationProps {
  totalDays: number;
  completedDays: number[];
  initialDay?: number;
  onDayChange?: (day: number) => void;
}

export function useDevotionalNavigation({
  totalDays,
  completedDays,
  initialDay,
  onDayChange
}: UseDevotionalNavigationProps) {
  // Calculate next available day based on sequential completion
  const calculateNextAvailableDay = () => {
    // If no completed days (e.g., not authenticated), return 1
    if (completedDays.length === 0) return 1;
    
    let day = 1;
    while (completedDays.includes(day) && day < totalDays) {
      day++;
    }
    return day;
  };

  const nextAvailableDay = calculateNextAvailableDay();
  
  // Use the initialDay if provided, otherwise default to nextAvailableDay
  const [currentDay, setCurrentDay] = useState(initialDay || nextAvailableDay || 1);
  const { toast } = useToast();

  // Effect to update current day when initialDay changes (like from URL params)
  useEffect(() => {
    if (initialDay && initialDay !== currentDay) {
      setCurrentDay(initialDay);
    }
  }, [initialDay]);

  // Effect to sync day changes with callback if provided
  useEffect(() => {
    onDayChange?.(currentDay);
  }, [currentDay, onDayChange]);

  // Check if all days up to a specific day are completed
  const areAllPreviousDaysCompleted = (day: number) => {
    // Always allow access to day 1
    if (day === 1) return true;
    
    // If no completed days (e.g., not authenticated), allow access to any day
    if (completedDays.length === 0) return true;

    // Check if all previous days are completed in sequence
    for (let i = 1; i < day; i++) {
      if (!completedDays.includes(i)) {
        return false;
      }
    }
    return true;
  };

  const canAccessDay = (day: number) => {
    // Can always access completed days
    if (completedDays.includes(day)) return true;
    
    // If no completed days (e.g., not authenticated), allow access to any day
    if (completedDays.length === 0) return true;
    
    // Can access next day only if all previous days are completed
    return areAllPreviousDaysCompleted(day);
  };

  const navigateDay = (direction: 'prev' | 'next') => {
    const newDay = direction === 'next' ? currentDay + 1 : currentDay - 1;
    
    if (newDay >= 1 && newDay <= totalDays) {
      // For authenticated users with progress, check access restrictions
      if (completedDays.length > 0 && !canAccessDay(newDay)) {
        toast({
          title: "Day Locked",
          description: "Please complete previous days first",
        });
        return;
      }
      setCurrentDay(newDay);
      onDayChange?.(newDay);
    }
  };

  return {
    currentDay,
    setCurrentDay,
    canAccessDay,
    navigateDay,
    nextAvailableDay,
    isFirstDay: currentDay === 1,
    isLastDay: currentDay === totalDays,
  };
}
